"use client";

import { trackEvent } from "@/lib/analytics";
import type { AnalyticsEventName } from "@/lib/analytics-events";
import { useLingui } from "@lingui/react";
import { DEFAULT_LOCALE, isLocale, localizeHref } from "@/lib/i18n/locales";

interface TrackedSignupLinkProps {
  readonly href: string;
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly intent: "login" | "signup";
  readonly source: string;
  /** Analytics event name. Defaults to "Auth Started" (the CTA-click funnel
   *  step); the /get-started explainer's button passes "Signup Continued" to
   *  measure the deeper explainer→Auth0 step distinctly. */
  readonly event?: AnalyticsEventName;
  /** Which provider the visitor picked on the /get-started selector ("google",
   *  "x", "email"). Rides along as an analytics prop so we can see what people
   *  actually choose — the funnel is otherwise blind past this click, since
   *  everything after it happens on Auth0. Omitted where there is no choice. */
  readonly connection?: string;
}

export function TrackedSignupLink({
  href,
  children,
  className,
  intent,
  source,
  event = "Auth Started",
  connection,
}: TrackedSignupLinkProps) {
  const { i18n } = useLingui();
  const locale = isLocale(i18n.locale) ? i18n.locale : DEFAULT_LOCALE;
  // Keep signup/get-started navigation inside /sv; auth/app hrefs pass through
  // (localizeHref only prefixes marketing paths).
  const localizedHref = localizeHref(href, locale);

  const track = () => {
    trackEvent(event, {
      href,
      intent,
      source,
      ...(connection ? { connection } : {}),
    });
  };

  return (
    <a
      href={localizedHref}
      className={className}
      onClick={track}
    >
      {children}
    </a>
  );
}
